import React, { useEffect } from "react";
import AppContainer from "../../common/app_container";
import { useOrganizationAPI } from "@src/api/organization.api";
import { Organization } from "@src/model/organization";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import Alert, { AlertProps } from "@src/common/alert";
import BackButton from "@src/common/components/back-button";

interface OrganizationEditProps { }

const OrganizationEdit: React.FC<OrganizationEditProps> = (props) => {
    const { id } = useParams();
    const organizationAPI = useOrganizationAPI();
    const navigate = useNavigate();
    const [status, setStatus] = React.useState<AlertProps | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [organizationTypes, setOrganizationTypes] = React.useState<string[]>([]);
    const [parentOrganizations, setParentOrganizations] = React.useState<Organization[]>([]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<Organization>();

    const onSubmit: SubmitHandler<Organization> = (data) => {
        setIsSaving(true);

        const savePromise = data.id
            ? organizationAPI.updateOrganization(data)
            : organizationAPI.createOrganization(data);

        savePromise
            .then(() => {
                setIsSaving(false);
                setStatus({ type: "success", message: "Organization saved successfully" });
                setTimeout(() => {
                    navigate("/organizations");
                }, 3000);
            })
            .catch((err) => {
                setIsSaving(false);
                setStatus({ type: "error", message: "Error saving organization: " + err.message });
            });
    };

    useEffect(() => {
        // Fetch organization types
        organizationAPI.getOrganizationTypes()
            .then((types) => {
                setOrganizationTypes(types);
            })
            .catch((error) => {
                setStatus({ type: "error", message: "Error loading organization types: " + error.message });
            });

        // Fetch parent organizations for dropdown
        organizationAPI.getAllOrganizations()
            .then((orgs) => {
                setParentOrganizations(orgs);
            })
            .catch((error) => {
                console.error("Error loading parent organizations:", error);
            });

        // Load organization data if editing
        if (id) {
            organizationAPI.getOrganization(id)
                .then((response) => {
                    reset(response);
                })
                .catch((error) => {
                    setStatus({ type: "error", message: "Error loading organization: " + error.message });
                    navigate("/organizations");
                });
        }
    }, [id, reset, navigate]);

    return (
        <AppContainer>
            <BackButton variant="ghost" onClick={() => navigate("/organizations")} />
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">{id ? "Edit Organization" : "Add Organization"}</h1>
                    <button
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        type="submit"
                        form="organization_form"
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                </div>

                {status && (
                    <div className="mb-4">
                        <Alert {...status} onClose={() => setStatus(null)} />
                    </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} id="organization_form" className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
                    <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 gap-x-6">
                        {/* Organization Name */}
                        <div className="sm:col-span-4">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Organization Name *
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("name", { required: "Organization name is required" })}
                                    type="text"
                                    id="name"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder="Organization Name"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                                )}
                            </div>
                        </div>

                        {/* Organization Type */}
                        <div className="sm:col-span-4">
                            <label htmlFor="organization_type" className="block text-sm font-medium text-gray-700">
                                Organization Type *
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("organization_type", { required: "Organization type is required" })}
                                    id="organization_type"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                >
                                    <option value="">-- Select Organization Type --</option>
                                    {organizationTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                                {errors.organization_type && (
                                    <p className="mt-1 text-sm text-red-600">{errors.organization_type.message}</p>
                                )}
                            </div>
                        </div>

                        {/* Description */}
                        <div className="sm:col-span-6">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <div className="mt-1">
                                <textarea
                                    {...register("description")}
                                    id="description"
                                    rows={3}
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder="Organization description"
                                />
                            </div>
                        </div>

                        {/* Is Active */}
                        <div className="sm:col-span-4">
                            <label htmlFor="is_active" className="block text-sm font-medium text-gray-700">
                                Status
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("is_active")}
                                    id="is_active"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>
                        </div>

                        {/* Parent Organization */}
                        <div className="sm:col-span-4">
                            <label htmlFor="parent_id" className="block text-sm font-medium text-gray-700">
                                Parent Organization
                            </label>
                            <div className="mt-1">
                                <select
                                    {...register("parent_id")}
                                    id="parent_id"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                >
                                    <option value="">-- No Parent Organization --</option>
                                    {parentOrganizations
                                        .filter(org => org.id !== id) // Filter out the current organization
                                        .map((org) => (
                                            <option key={org.id} value={org.id}>
                                                {org.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        {/* Logo Image URL */}
                        <div className="sm:col-span-4">
                            <label htmlFor="logo_image" className="block text-sm font-medium text-gray-700">
                                Logo Image URL
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("logo_image")}
                                    type="text"
                                    id="logo_image"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder="https://example.com/logo.png"
                                />
                            </div>
                        </div>

                        {/* Phone */}
                        <div className="sm:col-span-3">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Phone
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("phone")}
                                    type="tel"
                                    id="phone"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                        </div>

                        {/* Website */}
                        <div className="sm:col-span-3">
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                                Website
                            </label>
                            <div className="mt-1">
                                <input
                                    {...register("website")}
                                    type="url"
                                    id="website"
                                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </AppContainer>
    );
};

export default OrganizationEdit;